import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import store, { RootState } from "../store";
import { activePurchasesType, LinkUploadStatus, QuotaType, ResourceType } from "../../interfaces/general";
import { isFolder } from "../../scripts/helpers";
import { SortOnChangeType } from "../../utils/component/sortDropdown/sortDropdown.interface";

export enum FileManagerView {
    grid,
    list
}

export interface FileManagerSliceStateInterface {
    contextMenuPosition: { x: number; y: number };
    contextMenuStatus: boolean;
    contextMenuItems: string[];
    view: FileManagerView;
    resources: ResourceType[] | undefined;
    filteredResources: ResourceType[];
    selectedResources: ResourceType[];
    versionResources?: ResourceType[];
    isSingleSelect: boolean;
    newFolderModalStatus: boolean;
    linkUploadModalStatus: boolean;
    renameModalStatus: boolean;
    cutModalStatus: boolean;
    informationModalStatus: boolean;
    trashConfirmationModalStatus: boolean;
    deleteConfirmationModalStatus: boolean;
    downloadConfirmationModalStatus: boolean;
    downloadModalStatus: boolean;
    publicLinkModalStatus: boolean;
    publicLinkFolderModalStatus: boolean;
    previewModalStatus: boolean;
    shareModalStatus: boolean;
    versioningModalStatus: boolean;
    shareCutModalStatus: boolean;
    unshareConfirmationModalStatus: boolean;
    quota: QuotaType | undefined;
    activePurchases: any | undefined;
    sort: SortOnChangeType;
    scrollId: string | undefined;
    nextUrl: string | undefined;
    pagination: {
        page: number;
        pageSize: number;
    };
    defaultSelectedResourse: {
        item: string[];
        update: boolean;
    };
}

const fileManagerSliceState: FileManagerSliceStateInterface = {
    contextMenuPosition: { x: 0, y: 0 },
    contextMenuStatus: false,
    contextMenuItems: ["NEW_FOLDER", "FILE_UPLOAD", "FOLDER_UPLOAD", "LINK_UPLOAD"],
    view: FileManagerView.grid,
    resources: undefined,
    filteredResources: [],
    selectedResources: [],
    versionResources: undefined,
    isSingleSelect: false,
    linkUploadModalStatus: false,
    newFolderModalStatus: false,
    renameModalStatus: false,
    cutModalStatus: false,
    informationModalStatus: false,
    publicLinkModalStatus: false,
    publicLinkFolderModalStatus: false,
    trashConfirmationModalStatus: false,
    deleteConfirmationModalStatus: false,
    downloadConfirmationModalStatus: false,
    downloadModalStatus: false,
    previewModalStatus: false,
    shareModalStatus: false,
    versioningModalStatus: false,
    shareCutModalStatus: false,
    unshareConfirmationModalStatus: false,
    quota: undefined,
    activePurchases: undefined,
    sort: { status: "+", value: "name" },
    scrollId: undefined,
    nextUrl: undefined,
    pagination: {
        page: 1,
        pageSize: 50
    },
    defaultSelectedResourse: {
        item: [],
        update: false
    }
};

export const fileManagerSlice = createSlice({
    name: "fileManager",
    initialState: fileManagerSliceState,
    reducers: {
        setContextMenuPosition: (state, action: PayloadAction<{ x: number; y: number }>) => {
            state.contextMenuPosition = action.payload;
        },
        setContextMenuStatus: (state, action: PayloadAction<boolean>) => {
            state.contextMenuStatus = action.payload;
        },
        setContextMenuItems: (state, action: PayloadAction<string[]>) => {
            state.contextMenuItems = action.payload;
        },
        setFileManagerView: (state, action: PayloadAction<FileManagerView>) => {
            state.view = action.payload;
        },
        setResources: (state, action: PayloadAction<ResourceType[] | undefined>) => {
            state.resources = action.payload;
        },
        setVersionResources: (state, action: PayloadAction<ResourceType[] | undefined>) => {
            state.versionResources = action.payload;
        },
        setAddToResources: (state, action: PayloadAction<ResourceType>) => {
            switch (isFolder(action.payload)) {
                case true:
                    state.resources = state.resources ? [action.payload, ...state.resources] : [action.payload];
                    break;
                case false:
                    state.resources = state.resources ? [...state.resources, action.payload] : [action.payload];
                    break;
            }
        },
        setUpdateResources: (state, action: PayloadAction<ResourceType>) => {
            state.resources = state.resources
                ? state.resources.map((item) => {
                      if (item.id === action.payload.id) {
                          return action.payload;
                      } else {
                          return item;
                      }
                  })
                : [action.payload];
        },
        setFilteredResources: (state, action: PayloadAction<ResourceType[]>) => {
            const uploadingLinkResource: ResourceType[] = action.payload
                ? [...action.payload].filter((a, b) => a.linkUploadStatus && ![LinkUploadStatus.DONE].includes(a.linkUploadStatus))
                : [];
            const recentResource: ResourceType[] = action.payload
                ? [...action.payload].filter((a, b) => !(a.linkUploadStatus && ![LinkUploadStatus.DONE].includes(a.linkUploadStatus)))
                : [];
            state.filteredResources = [...uploadingLinkResource, ...recentResource];
        },
        setSelectedResources: (state, action: PayloadAction<ResourceType[]>) => {
            state.selectedResources = action.payload.filter(
                (item) => !(item.linkUploadStatus && ![LinkUploadStatus.DONE].includes(item.linkUploadStatus))
            );
        },
        setIsSingleSelect: (state, action: PayloadAction<boolean>) => {
            state.isSingleSelect = action.payload;
        },
        setNewFolderModalStatus: (state, action: PayloadAction<boolean>) => {
            state.newFolderModalStatus = action.payload;
        },
        setLinkUploadModalStatus: (state, action: PayloadAction<boolean>) => {
            state.linkUploadModalStatus = action.payload;
        },
        setRenameModalStatus: (state, action: PayloadAction<boolean>) => {
            state.renameModalStatus = action.payload;
        },
        setCutModalStatus: (state, action: PayloadAction<boolean>) => {
            state.cutModalStatus = action.payload;
        },
        setInformationModalStatus: (state, action: PayloadAction<boolean>) => {
            state.informationModalStatus = action.payload;
        },
        setTrashConfirmationModalStatus: (state, action: PayloadAction<boolean>) => {
            state.trashConfirmationModalStatus = action.payload;
        },
        setDeleteConfirmationModalStatus: (state, action: PayloadAction<boolean>) => {
            state.deleteConfirmationModalStatus = action.payload;
        },
        setDownloadConfirmationModalStatus: (state, action: PayloadAction<boolean>) => {
            state.downloadConfirmationModalStatus = action.payload;
        },
        setDownloadModalStatus: (state, action: PayloadAction<boolean>) => {
            state.downloadModalStatus = action.payload;
        },
        setPreviewModalStatus: (state, action: PayloadAction<boolean>) => {
            state.previewModalStatus = action.payload;
        },
        setShareModalStatus: (state, action: PayloadAction<boolean>) => {
            state.shareModalStatus = action.payload;
        },
        setVersioningModalStatus: (state, action: PayloadAction<boolean>) => {
            state.versioningModalStatus = action.payload;
        },
        setShareCutModalStatus: (state, action: PayloadAction<boolean>) => {
            state.shareCutModalStatus = action.payload;
        },
        setUnshareConfimrationModalStatus: (state, action: PayloadAction<boolean>) => {
            state.unshareConfirmationModalStatus = action.payload;
        },
        setPublicLinkStatus: (state, action: PayloadAction<boolean>) => {
            state.publicLinkModalStatus = action.payload;
        },
        setPublicLinkFolderStatus: (state, action: PayloadAction<boolean>) => {
            state.publicLinkFolderModalStatus = action.payload;
        },
        setQuota: (state, action: PayloadAction<QuotaType>) => {
            state.quota = action.payload;
        },
        setActivePurchases: (state, action: PayloadAction<activePurchasesType>) => {
            state.activePurchases = action.payload;
        },
        setSort: (state, action: PayloadAction<SortOnChangeType>) => {
            state.sort = action.payload;
        },
        setScrollId: (state, action: PayloadAction<string | undefined>) => {
            state.scrollId = action.payload;
        },
        setNextUrl: (state, action: PayloadAction<string | undefined>) => {
            state.nextUrl = action.payload;
        },
        setPagination: (state, action: PayloadAction<{ page: number; pageSize: number }>) => {
            state.pagination = action.payload;
        },
        setDefaultSelectedResourse: (state, action: PayloadAction<{ item: string[]; update: boolean }>) => {
            state.defaultSelectedResourse = action.payload;
        }
    }
});

export const {
    setContextMenuStatus,
    setContextMenuPosition,
    setContextMenuItems,
    setFileManagerView,
    setResources,
    setVersionResources,
    setFilteredResources,
    setAddToResources,
    setUpdateResources,
    setSelectedResources,
    setIsSingleSelect,
    setNewFolderModalStatus,
    setLinkUploadModalStatus,
    setRenameModalStatus,
    setCutModalStatus,
    setQuota,
    setSort,
    setInformationModalStatus,
    setDeleteConfirmationModalStatus,
    setDownloadConfirmationModalStatus,
    setDownloadModalStatus,
    setTrashConfirmationModalStatus,
    setPreviewModalStatus,
    setShareModalStatus,
    setShareCutModalStatus,
    setVersioningModalStatus,
    setPublicLinkStatus,
    setPublicLinkFolderStatus,
    setUnshareConfimrationModalStatus,
    setScrollId,
    setNextUrl,
    setPagination,
    setDefaultSelectedResourse,
    setActivePurchases
} = fileManagerSlice.actions;
export const selectFileManager = (state: RootState) => state.fileManager;
export default fileManagerSlice.reducer;
